import { Subscription, SubscriptionPayload, SubscriptionAllDetails, SubscriptionPaymentPayload, RefundCalculationDetails, RefundPaymentPayload, RefundPaymentResponse } from './subscriptionType';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

class SubscriptionApi {

    static async createSubscriptionPayment(subscriptionPayment: SubscriptionPaymentPayload) {
        try {
            const response = await fetch(`${API_BASE_URL}/subscriptions/create_subscription_payment`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    payment_method_id: subscriptionPayment.payment_method_id,
                    amount: subscriptionPayment.amount,
                    transaction_type: subscriptionPayment.transaction_type,
                    event_type: subscriptionPayment.event_type,
                    plan_name: subscriptionPayment.plan_name,
                    number_months: subscriptionPayment.number_months
                }),
                credentials: 'include',
            });

            const responseBody = await response.json();

            if (response.status === 201) {
                return responseBody;
            }
            else if (response.status === 401) {
                throw new Error(`${response.status}: Session expired. Try to refresh your browser.`);
            }
            else {
                throw new Error(`${responseBody.error} status ${response.status}`);
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to create subscription payment: ${error.message}`);
            } else {
                throw new Error('Failed to create subscription payment: An unknown error occurred.');
            }
        }
    }

    static async extendSubscriptionPayment(subscriptionPayment: SubscriptionPaymentPayload) {
        try {
            const response = await fetch(`${API_BASE_URL}/subscriptions/extend_subscription_payment`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    payment_method_id: subscriptionPayment.payment_method_id,
                    amount: subscriptionPayment.amount,
                    transaction_type: subscriptionPayment.transaction_type,
                    event_type: subscriptionPayment.event_type,
                    plan_name: subscriptionPayment.plan_name,
                    number_months: subscriptionPayment.number_months
                }),
                credentials: 'include',
            });

            const responseBody = await response.json();

            if (response.status === 201) {
                return responseBody;
            }
            else if (response.status === 401) {
                throw new Error(`${response.status}: Session expired. Try to refresh your browser.`);
            }
            else {
                throw new Error(`${responseBody.error} status ${response.status}`);
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to extend subscription payment: ${error.message}`);
            } else {
                throw new Error('Failed to extend subscription payment: An unknown error occurred.');
            }
        }
    }


static async getSubscriptionAndAll(): Promise<SubscriptionAllDetails> {
    try {
        const response = await fetch(`${API_BASE_URL}/subscriptions/subscription_all_details`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
        });

        // Check status first before parsing JSON
        if (response.status === 401) {
            throw new Error(`${response.status}: Session expired. Try to refresh your browser.`);
        }

        if (response.status === 404) {
            return {
                subscription: null,
                subscription_events: [],
                subscription_payments: []
            };
        }

        // Check if response has content before parsing
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
            throw new Error(`Expected JSON response but got ${contentType}`);
        }

        const text = await response.text();
        if (!text || text.trim() === '') {
            throw new Error('Empty response from server');
        }

        const responseBody = JSON.parse(text);

        if (response.status === 200) {
            return {
                subscription: responseBody.subscription?.data?.attributes || null,
                subscription_events: responseBody.subscription_events?.data?.map((item: any) => item.attributes) || [],
                subscription_payments: responseBody.subscription_payments?.data?.map((item: any) => item.attributes) || []
            };
        }
        else {
            throw new Error(responseBody.error || `Error: Status ${response.status}`);
        }
    } catch (error) {
        if (error instanceof Error) {
            throw new Error(`Failed to fetch subscription details: ${error.message}`);
        } else {
            throw new Error('Failed to fetch subscription details: An unknown error occurred.');
        }
    }
}


    static async getPreviewCancellation(subscriptionId: string) {
        try {
            const response = await fetch(`${API_BASE_URL}/subscriptions/${subscriptionId}/preview_cancellation`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const responseBody = await response.json();

            if (response.status === 200) {
                return responseBody;
            }
            else if (response.status === 401) {
                throw new Error(`${response.status}: Session expired. Try to refresh your browser.`);
            }
            else {
                throw new Error(responseBody.error || `Error: Status ${response.status}`);
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to fetch cancellation preview: ${error.message}`);
            } else {
                throw new Error('Failed to fetch cancellation preview: An unknown error occurred.');
            }
        }
    }

    // NEW: Calculate refundable amount for a specific payment
    static async calculateRefundableAmount(paymentIntentId: string): Promise<RefundCalculationDetails> {
        try {
            const response = await fetch(`${API_BASE_URL}/subscriptions/calculate_refundable_amount?payment_intent_id=${paymentIntentId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            const responseBody = await response.json();

            if (response.status === 200) {
                return responseBody;
            }
            else if (response.status === 401) {
                throw new Error(`${response.status}: Session expired. Try to refresh your browser.`);
            }
            else {
                throw new Error(responseBody.error || `Error: Status ${response.status}`);
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to calculate refundable amount: ${error.message}`);
            } else {
                throw new Error('Failed to calculate refundable amount: An unknown error occurred.');
            }
        }
    }

    // NEW: Process refund for subscription payment
    static async refundSubscriptionPayment(refundData: RefundPaymentPayload): Promise<RefundPaymentResponse> {
        try {
            const response = await fetch(`${API_BASE_URL}/subscriptions/refund_subscription_payment`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    payment_intent_id: refundData.payment_intent_id,
                    number_months: refundData.number_months,
                    refund_amount: refundData.refund_amount
                }),
                credentials: 'include',
            });

            const responseBody = await response.json();

            if (response.status === 200 || response.status === 201) {
                return responseBody;
            }
            else if (response.status === 401) {
                throw new Error(`${response.status}: Session expired. Try to refresh your browser.`);
            }
            else {
                throw new Error(responseBody.error || `Error: Status ${response.status}`);
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to process refund: ${error.message}`);
            } else {
                throw new Error('Failed to process refund: An unknown error occurred.');
            }
        }
    }

}
export default SubscriptionApi;